import type { CalibrationRunStatus } from './types';

// Mirrors aistudio's `IN_PROGRESS_STATUSES` in client/views/Calibration/constants.
// Keep in sync — the row-actions menu and any "still running" affordance key
// off this list. NOT the same set the host polling banner understands: see
// `HOST_BANNER_IN_PROGRESS_STATUSES` below.
export const IN_PROGRESS_STATUSES = [
  'IN_PROGRESS',
  'SENT_FOR_PREPROCESSING',
  'SENT_TO_ANALYTICS',
  'ANALYTICS_PROCESSING_COMPLETED',
  'PREDICTION_STARTED',
  'PREDICTION_COMPLETED',
  'SENDING_TO_DS',
] as const;

// The in-progress statuses aistudio's own polling banner understands — the
// keys of `CalibrationRunPollingInfo.msg` in
// client/services/constants/polling-constants.js (host-owned, not derivable
// from this repo). Deliberately excludes `PREDICTION_COMPLETED`: the host's
// map has no entry for it, so `PollingBanner.setBannerState` would fall to
// `default`, which blanks the message and calls `stopPolling` — killing the
// host's own completion polling for a run the user is actively watching.
const HOST_BANNER_IN_PROGRESS_STATUSES = IN_PROGRESS_STATUSES.filter(
  (status) => status !== 'PREDICTION_COMPLETED'
);

// A run in one of these statuses hasn't produced a result yet but already
// occupies the bot's single run slot: it blocks Create Run, offers Cancel
// rather than Delete, and keeps event-status polling armed. Shared with
// `matchesStatusFilter`'s SCHEDULED bucket below.
//
// Deliberate divergence from aistudio, whose `isInProgressStatus` is
// `IN_PROGRESS_STATUSES || SCHEDULED`: there an INITIATED run offers Delete and
// doesn't block Create Run. Both UIs are live (legacy Vue for non-EP bots, this
// remote for EP bots), so the two disagree on INITIATED until aistudio takes
// the matching change.
const PRE_RUN_STATUSES = ['SCHEDULED', 'INITIATED'] as const;

export function isInProgressStatus(status: CalibrationRunStatus | string | undefined): boolean {
  if (!status) return false;
  return (
    (IN_PROGRESS_STATUSES as readonly string[]).includes(status) ||
    (PRE_RUN_STATUSES as readonly string[]).includes(status)
  );
}

/**
 * Does aistudio's polling banner understand this status?
 *
 * `PollingBanner.setBannerState` switches on `CalibrationRunPollingInfo.state`,
 * which covers `HOST_BANNER_IN_PROGRESS_STATUSES` plus the three terminal
 * states. Anything else falls to `default`, which blanks the message and
 * calls `stopPolling` — so notifying the host with a `PRE_RUN_STATUSES` run,
 * or with `PREDICTION_COMPLETED`, would kill polling that a freshly created
 * run just started.
 */
export function isHostBannerStatus(status: CalibrationRunStatus | string | undefined): boolean {
  if (!status) return false;
  return (
    (HOST_BANNER_IN_PROGRESS_STATUSES as readonly string[]).includes(status) ||
    status === 'COMPLETED' ||
    isUnsuccessfulStatus(status)
  );
}

// Terminal states that never produce a report: no recommendations, no row navigation.
// Mirrors aistudio's `isUnsuccessfulStatus` in client/views/Calibration/constants.
export function isUnsuccessfulStatus(status: CalibrationRunStatus | string | undefined): boolean {
  return status === 'FAILED' || status === 'CANCELLED';
}

/** The only status a run can have and still have a report to open. */
export function isCompletedStatus(status: CalibrationRunStatus | string | undefined): boolean {
  return status === 'COMPLETED';
}

/**
 * Status filter values shown in the run-list toolbar. These are filter *buckets*,
 * not raw statuses — "In Progress" stands for the whole `IN_PROGRESS_STATUSES`
 * family, which is why the filter can't compare a run's status directly.
 * Mirrors aistudio's RunList.vue `filteredRuns` + MiddleSection's statusOptions.
 */
export const RUN_STATUS_FILTERS = {
  SCHEDULED: 'SCHEDULED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
} as const;

/**
 * Does `status` belong to the given filter bucket?
 *
 * "In Progress" expands to every intermediate backend status (a run reports
 * SENT_TO_ANALYTICS, PREDICTION_STARTED, … while running, never the literal
 * 'IN_PROGRESS' for most of its life), so an equality check silently matched
 * nothing. "Scheduled" also accepts INITIATED, which this app's own status
 * union and badge recognise as the same pre-run state.
 */
export function matchesStatusFilter(
  filterValue: string,
  status: CalibrationRunStatus | string | undefined
): boolean {
  if (!status) return false;
  if (filterValue === RUN_STATUS_FILTERS.IN_PROGRESS) {
    return (IN_PROGRESS_STATUSES as readonly string[]).includes(status);
  }
  if (filterValue === RUN_STATUS_FILTERS.SCHEDULED) {
    return (PRE_RUN_STATUSES as readonly string[]).includes(status);
  }
  return filterValue === status;
}
